import { Stack, Typography, Box } from '@mui/material';
import CustomSnackbar from 'components/CustomSnackbar';
import { SensorAlert } from 'components/Alerts/SensorAlert';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const ALERTS_GAP = '0.7rem';
const ALERT_ITEM_WIDTH_MD_UP = 'calc(50% - 0.35rem)';
const ALERT_ITEM_WIDTH_XL_UP = 'calc((100% - 1.4rem) / 3)';

export const AlertSummary = props => {
  const devices = useSelector(state => state.sensors.devices);
  const dispatch = useDispatch();
  const alerts = useSelector(state => state.sensors.alerts);
  const sensorName = useSelector(state => state.sensors.data.sensorName);
  const alertSummaryRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  // console.log("alerts:", alerts);
  const [snackbarState, setSnackbarState] = useState({
    snackbarOpen: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal, snackbarOpen } = snackbarState;

  const timeRange = useSelector(state => state.sensors.timeRange);

  useEffect(() => {
    const updateAvailableHeight = () => {
      if (!alertSummaryRef.current) return;

      const { top } = alertSummaryRef.current.getBoundingClientRect();
      const parentPaddingBottom = parseFloat(window.getComputedStyle(alertSummaryRef.current.parentElement).paddingBottom);

      setAvailableHeight(Math.max(0, Math.floor(window.innerHeight - top - parentPaddingBottom - 1)));
    };

    const animationFrameId = window.requestAnimationFrame(updateAvailableHeight);
    window.addEventListener('resize', updateAvailableHeight);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', updateAvailableHeight);
    };
  }, [alerts, timeRange]);

  const toEventArray = alertEntry => {
    if (!alertEntry) return [];
    if (Array.isArray(alertEntry.ranges)) return alertEntry.ranges;
    if (alertEntry.start && alertEntry.end) {
      return [
        {
          start: alertEntry.start,
          end: alertEntry.end,
          start_unix: alertEntry.start_unix,
          end_unix: alertEntry.end_unix,
          color: alertEntry.color,
          description: alertEntry.description,
        },
      ];
    }
    return [];
  };

  const getAlertFromProps = (alertType, rangesArray, key) => {
    const normalizedRanges = Array.isArray(rangesArray) ? rangesArray : [];
    // const dateString = timestamp.slice(0, -1);
    if (alertType === 'Disconnected' || alertType === 'Reconnected') {
      return;
    }
    if (!normalizedRanges.length) {
      return null;
    }
    // console.log("alertType:", alertType);
    // console.log("rangesArray:", rangesArray);
    return (
      <Box
        key={key ?? alertType}
        sx={{
          flex: {
            xs: '0 0 100%',
            md: `0 0 ${ALERT_ITEM_WIDTH_MD_UP}`,
            xl: `0 0 ${ALERT_ITEM_WIDTH_XL_UP}`,
          },
          maxWidth: {
            xs: '100%',
            md: ALERT_ITEM_WIDTH_MD_UP,
            xl: ALERT_ITEM_WIDTH_XL_UP,
          },
          minWidth: 0,
          '& > .MuiPaper-root': {
            width: '100% !important',
            marginBottom: '0 !important',
            boxShadow: ({ boxShadows }) => boxShadows.md,
            overflow: 'visible',
          },
        }}>
        <SensorAlert
          description={alertType}
          sensorId={props.sensorId}
          showSensorId={props.showSensorId ?? true}
          alertsNum={normalizedRanges.length}
          alertTimes={normalizedRanges}
        />
      </Box>
    );
  };

  const formatTimeAndDate = timeString => {
    // Parse the ISO string into a Date object
    const date = new Date(timeString);

    // Format the time (HH:mm) using UTC values
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    const formattedTime = `${hours}:${minutes}`;

    // Format the date (dd.MM.yyyy) using UTC values
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed
    const year = date.getUTCFullYear();
    const formattedDate = `${day}.${month}.${year}`;

    return [formattedTime, formattedDate];
  };

  const handleDownloadAlertsCSV = () => {
    const isEmpty = Object.keys(alerts).length === 0;
    if (isEmpty) {
      setSnackbarState({ ...snackbarState, snackbarOpen: true });
      return;
    }

    // const data = {
    //   'Bad - Uncomfortable Enviorment': [1740799897, 1740797455, 1740790564, 1740740754],
    //   'Bad Air Quality - High TVOC': [1741550752, 1741548499, 1741547888],=
    // };

    let csvContent = `Sol-In Technologies - Indoor Air Quality,\nsensor id, ${props.sensorId}, ${sensorName},\nalert type, start time, start date, end time, end date,\n`;

    Object.entries(alerts).forEach(alertAndTimesObj => {
      const alertName = alertAndTimesObj[1].event_type;
      const ranges = toEventArray(alertAndTimesObj[1]);

      // csvContent += ``;

      for (let i = 0; i < ranges.length; i++) {
        let [startTime, startDate] = formatTimeAndDate(ranges[i].start);
        let [endTime, endDate] = formatTimeAndDate(ranges[i].end);

        // prettier-ignore

        csvContent += `${alertName}, ${startTime}, ${startDate}, ${endTime}, ${endDate},\n`;
      }
    });

    // Create a Blob from the CSV string
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    // Create a temporary link element
    const link = document.createElement('a');
    // Check if the download attribute is supported
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${props.sensorId}-alerts`);
      link.style.visibility = 'hidden';
      // Append the link to the DOM, trigger the click, then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getAlerts = () => {
    if (props.sensorId === false) {
      // Handle multi-sensor case - needs updating too
      return Object.entries(alerts).map(alertsBySensor => {
        return alertsBySensor[1].map((alert, index) => {
          return getAlertFromProps(alert.event_type, [alert], `${alertsBySensor[0]}-${alert.event_type}-${index}`);
        });
      });
    } else {
      if (Array.isArray(alerts) && alerts.length > 0) {
        // Group events by type
        const groupedAlerts = alerts.reduce((acc, alert) => {
          if (!acc[alert.event_type]) {
            acc[alert.event_type] = [];
          }
          acc[alert.event_type].push({
            start: alert.start,
            end: alert.end,
            start_unix: alert.start_unix,
            end_unix: alert.end_unix,
            color: alert.color,
          });
          return acc;
        }, {});

        return Object.entries(groupedAlerts).map(([eventType, eventList]) => {
          return getAlertFromProps(eventType, eventList, eventType);
        });
      }
    }
  };

  useEffect(() => {
    if (props.downloadCsv !== 0) {
      handleDownloadAlertsCSV();
    }
  }, [props.downloadCsv]);

  const handleSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, snackbarOpen: false });
  };

  return (
    <>
      <Stack
        ref={alertSummaryRef}
        // spacing={1}
        mt={2.5}
        // mb={2}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: {
            xs: 'wrap',
          },
          gap: ALERTS_GAP,
          width: {
            xs: '100%',
          },
          flex: '1 1 auto',
          minHeight: 0,
          alignItems: {
            xs: 'center',
            lg: 'flex-start',
          },
          justifyContent: 'flex-start',
          // height: availableHeight === null ? 'auto' : `${availableHeight}px`,
          // maxHeight: availableHeight === null ? 'none' : `${availableHeight}px`,
          height: 'fit-content',
          maxHeight: 'fit-content',
          overflow: 'visible',
        }}>
        <CustomSnackbar
          open={snackbarState.snackbarOpen}
          duration={2500}
          onClose={handleSnackbarClose}
          message="No alerts for the selected time range. "
          vertical={vertical}
          horizontal={horizontal}
          sx={{ marginTop: '2.5rem' }}
        />
        {getAlerts()}
      </Stack>
    </>
  );
};
